import { type Ref, useEffect, useRef } from 'react';
import type { AccountHub } from '@fanduel/cpe-um-bundle/account-hub/library';
import { AccountHubAction, type IAccountHubNavigationDelegate } from '@fanduel/account-hub-contract';
import { getPlatformBridge } from '@/logic/bridges';
import { type RootState, useStore } from '@/store/store';
import { usePathname, useRouter } from 'next/navigation';
import { useGlobalContext } from '@/store/contexts/global-context';

enum AccountHubDelegateId {
    LOYALTY_PARTNERS = 'LOYALTY_PARTNERS',
    CASINO_RAF = 'CASINO_RAF',
    CASINO_BONUS = 'CASINO_BONUS',
    CASINO_REWARDS_CLUB = 'CASINO_REWARDS_CLUB'
}

export const useAccountHubUM = () => {
    const accountHubManager: Ref<typeof AccountHub> = useRef(null);
    const setShowBalance = useStore((state: RootState) => state.userContext.accountSettings.setShowBalance);
    const handleShowLoading = useStore((state: RootState) => state.loading.handleShowLoading);
    const handleHideLoading = useStore((state: RootState) => state.loading.handleHideLoading);
    const accountHubModal = useStore((state: RootState) => state.accountHubModals.accountHub);
    const { isAndroidWrapper } = useGlobalContext();
    const router = useRouter();
    const pathname = usePathname();

    const setupDelegate = () => {
        if (!accountHubManager.current) return;

        const navigateTo = (targetPath: string): boolean => {
            if (pathname === targetPath) {
                handleHideLoading();
                accountHubManager.current?.dismissAll();
                return true;
            }

            handleShowLoading();
            router.push(targetPath);
            accountHubManager.current?.dismissAll();
            return true;
        };

        const navigationDelegate: IAccountHubNavigationDelegate = {
            // eslint-disable-next-line
            navigate: (pageId: String): boolean => {
                const pageIdStr = pageId.toString();

                switch (pageIdStr) {
                    case AccountHubDelegateId.LOYALTY_PARTNERS:
                        return navigateTo('/momentum');

                    case AccountHubDelegateId.CASINO_RAF:
                        return navigateTo('/refer');

                    case AccountHubDelegateId.CASINO_BONUS:
                        return navigateTo('/rewards');

                    case AccountHubDelegateId.CASINO_REWARDS_CLUB:
                        return navigateTo('/rewards');

                    default:
                        return false;
                }
            },
            // eslint-disable-next-line
            canHandle: (pageId: String): boolean => {
                const supportedDelegates = Object.values(AccountHubDelegateId) as AccountHubDelegateId[];
                return supportedDelegates.includes(pageId as AccountHubDelegateId);
            }
        };

        accountHubManager.current.delegate(navigationDelegate);
    };

    const openAccountHub = () => {
        if (isAndroidWrapper) {
            const platformBridge = getPlatformBridge();
            platformBridge.exec('openAccountHub');
            return;
        }

        if (!accountHubManager.current) {
            return;
        }

        accountHubModal.setIsOpen(true);
        accountHubManager.current.presentAccountHubAsync();
    };

    const initializeAccountHubUM = async () => {
        if (isAndroidWrapper) {
            const platformBridge = getPlatformBridge();

            try {
                const result = platformBridge.exec('getShowBalance') as boolean | undefined;
                if (typeof result === 'boolean') {
                    setShowBalance(result);
                }
            } catch (e) {
                console.error('[AccountHub Android] getShowBalance failed', e);
            }

            return;
        }

        const { initializeUnifiedModules } = await import('@/logic/unified-modules');
        await initializeUnifiedModules();

        accountHubManager.current = (await import('@fanduel/cpe-um-bundle/account-hub/library')).AccountHub;

        setupDelegate();

        setShowBalance(accountHubManager.current?.getShowBalance());

        const listener = (event?: { action: string }) => {
            setShowBalance(accountHubManager.current?.getShowBalance());

            if (event?.action === AccountHubAction.AccountHubEnded) {
                accountHubModal.setIsOpen(false);
            }
        };

        const removeListener = accountHubManager.current.addListener(listener);

        return () => {
            removeListener();
        };
    };

    useEffect(() => {
        let cleanup: (() => void) | undefined;

        (async () => {
            cleanup = await initializeAccountHubUM();
        })();

        return () => {
            cleanup?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        openAccountHub
    };
};
